import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { GithubDeploymentAuthRequest } from '../types';

/**
 * Hook to fetch infrastructure apps
 */
export const useApps = () => {
  return useQuery({
    queryKey: ['apps'],
    queryFn: () => apiClient.getApps(),
  });
};

/**
 * Hook to fetch deployments
 */
export const useDeployments = () => {
  return useQuery({
    queryKey: ['deployments'],
    queryFn: () => apiClient.getDeployments(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

/**
 * Hook to fetch plan results
 */
export const usePlanResults = () => {
  return useQuery({
    queryKey: ['planResults'],
    queryFn: () => apiClient.getPlanResults(),
    refetchInterval: 60000, // Refetch every minute
  });
};

/**
 * Hook to fetch verification results
 */
export const useVerificationResults = () => {
  return useQuery({
    queryKey: ['verificationResults'],
    queryFn: () => apiClient.getVerificationResults(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

/**
 * Hook to set verification status (mutation)
 */
export const useSetVerificationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (deploymentId: number) => apiClient.setVerificationStatus(deploymentId, 'success'),
    onSuccess: () => {
      // Invalidate verification results to refetch
      queryClient.invalidateQueries({ queryKey: ['verificationResults'] });
    },
  });
};

/**
 * Hook to update app configuration (mutation)
 */
export const useUpdateApp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appName,
      environments,
      validationInstructions,
      bulkDeploymentExclusions,
    }: {
      appName: string;
      environments: string[];
      validationInstructions?: Record<string, { info?: string }>;
      bulkDeploymentExclusions?: string[];
    }) =>
      apiClient.updateApp(appName, environments, validationInstructions, bulkDeploymentExclusions),
    onSuccess: () => {
      // Invalidate apps to refetch
      queryClient.invalidateQueries({ queryKey: ['apps'] });
    },
  });
};

export const useDispatchGithubDeployments = () => {
  return useMutation({
    mutationFn: (request: GithubDeploymentAuthRequest) =>
      apiClient.dispatchGithubDeployments(request),
  });
};
